import type { AssetWorkflowOptions } from '../../types';

export const editFulfillmentContextWorkflow: AssetWorkflowOptions = {
  workflowKey: 'editFulfillmentContextWorkflow',
  workflowType: 'editAssetWorkflow',
  assetType: 'fulfillmentContext',
  workflowIndex: 1,
  workflowName: 'Edit a Fulfillment Context',
  workflowDescription: `Edit a fulfillment context that you have set up.`,
  overrideSave: 'editFulfillmentContext',
  workflowStepOptions: [
    {
      workflowStepKey: 'partnerProvidedId',
      workflowStepNavigationName: 'Partner Provided Fulfillment Context ID',
      optional: true,
      workflowComponent: {
        workflowComponentType: 'textEntry',
        prompt: 'Enter a unique identifier for this fulfillment context',
        description:
          'This is optional but will allow you to more easily reference this fulfillment context in API calls.',
        help: '',
      },
      includeInDefaultAssetNameRules: {
        includeInDefaultAssetName: true,
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'partnerProvidedId',
      },
    },
    {
      workflowStepKey: 'name',
      workflowStepNavigationName: 'Fulfillment Context Name',
      workflowComponent: {
        workflowComponentType: 'textEntry',
        prompt: 'What should this fulfillment context be named?',
        description: ``,
        help: '',
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'name',
      },
    },
    {
      workflowStepKey: 'Description',
      workflowStepNavigationName: 'Fulfillment Context Description',
      workflowComponent: {
        workflowComponentType: 'textEntry',
        rows: 10,
        prompt: 'Enter a description (optional):',
        description: ``,
        defaultValue: '',
        help: '',
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'description',
      },
    },
    /// TODO: express this in a way that people other than John might understand
    {
      workflowStepKey: 'contextType',
      workflowStepNavigationName: 'Fulfillment Context Type',
      workflowComponent: {
        workflowComponentType: 'fctxTypeSelect',
        prompt: 'What kind of fulfillment context is this?:',
        description: ``,
        defaultValue: '',
        help: '',
        options: [
          { optionText: 'Customer', optionValue: 'customer' },
          { optionText: 'Hundredweight', optionValue: 'hundredweight' },
          { optionText: 'Returns', optionValue: 'returns' },
          { optionText: 'At Large', optionValue: 'at_large' },
        ],
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'fulfillmentType',
      },
    },
    {
      workflowStepKey: 'fulfillmentCenterOrigin',
      workflowStepNavigationName: 'Origins',
      excludeFromNavigation: false,
      workflowComponent: {
        workflowComponentType: 'originSelect',
        prompt: 'Which origins will be used for this fulfillment context?',
        description: '',
        help: '',
        defaultValue: null,
        requiredLinks: ['schedule', 'address', 'contact'],
      },
      workflowStateToAssetMapping: {
        mappingType: 'link',
        linkAssetType: 'origin',
      },
    },
    {
      workflowStepKey: 'fulfillmentCenterContracts',
      workflowStepNavigationName: 'Contracts',
      excludeFromNavigation: false,
      workflowComponent: {
        workflowComponentType: 'contractSelect',
        prompt: 'Which contracts will be used for this fulfillment context?',
        description: '',
        help: '',
        defaultValue: null,
        options: [],
      },
      workflowStateToAssetMapping: {
        mappingType: 'link',
        list: true,
        linkAssetType: 'contract',
      },
    },
    {
      workflowStepKey: 'fulfillmentCenterReturnAddressOverrides',
      workflowStepNavigationName: 'Return To Address Overrides',
      excludeFromNavigation: false,
      workflowComponent: {
        workflowComponentType: 'returnToAddressSelect',
        prompt: '',
        description: 'What return to address overrides will be used for this fulfillment context?',
        help: '',
        defaultValue: null,
        options: [],
        optional: true,
      },
      workflowStateToAssetMapping: {
        mappingType: 'link',
        linkAssetType: 'returnAddressOverride',
      },
    },
    {
      workflowStepKey: 'fulfillmentCenterReturnsSettings',
      workflowStepNavigationName: 'Returns Setting',
      excludeFromNavigation: false,
      workflowComponent: {
        workflowComponentType: 'returnsSettingSelect',
        prompt: 'What settings will be used for returns?',
        description: '',
        help: '',
        options: [],
        defaultValue: null,
      },
      workflowStepConditionalMatcher: [
        { workflowStepKey: 'contextType', schema: { type: 'string', const: 'returns' } },
      ],
      workflowStateToAssetMapping: {
        mappingType: 'link',
        linkAssetType: 'returnsSettings',
      },
    },
    {
      workflowStepKey: 'fulfillmentCenterLabelProperties',
      workflowStepNavigationName: 'Label Properties',
      excludeFromNavigation: false,
      workflowComponent: {
        workflowComponentType: 'labelPropertiesSelect',
        prompt: 'Which labels properties apply in this fulfillment context?',
        description: '',
        help: '',
      },
      workflowStateToAssetMapping: {
        mappingType: 'link',
        linkAssetType: 'labelProperties',
      },
    },
    {
      workflowStepKey: 'labelAugmentations',
      workflowStepNavigationName: 'Label Augmentations',
      excludeFromNavigation: false,
      workflowComponent: {
        workflowComponentType: 'labelAugmentationSelect',
        prompt: 'Which label augmentation will apply in this fulfillment context?',
        description: '',
        help: '',
        defaultValue: null,
        options: [],
      },
      workflowStateToAssetMapping: {
        mappingType: 'link',
        linkAssetType: 'augmentationSet',
      },
    },
    {
      workflowStepKey: 'tagEntry',
      workflowStepNavigationName: 'Add Tags',
      workflowComponent: {
        workflowComponentType: 'tagEntry',
        prompt: 'Tag this context?',
        description: 'Add tags to this fulfillment context',
        help: '',
        defaultValue: null,
      },
      workflowStateToAssetMapping: {
        mappingType: 'link',
        linkAssetType: 'tags',
        list: true,
      },
    },
    {
      workflowStepKey: 'tenantSelect',
      workflowStepNavigationName: 'Tenant Select',
      workflowComponent: {
        assetType: 'tenant',
        workflowComponentType: 'tenantSelect',
        prompt: 'Does this fulfillment context belong to a specific tenant?',
        description: '',
        help: '',
        optional: true,
      },
      calculatedComponentProps: {
        assetId: {
          type: 'asset',
          property: 'fulfillmentContextId',
        },
      },
      optional: true,
      workflowStepConditionalMatcher: [
        {
          assetType: 'tenant',
          schema: { type: 'array', minItems: 1 },
        },
      ],
      workflowStateToAssetMapping: {
        mappingType: 'link',
        linkAssetType: 'tenant',
        linkType: 'Tenant',
      },
    },
    // holding off until we are ready for release
    {
      workflowStepKey: 'assetSetSelect',
      workflowStepNavigationName: 'Third Party Billing Group Select',
      optional: true,
      workflowComponent: {
        workflowComponentType: 'assetSetSelect',
        assetType: 'thirdPartyBillingAccountSet',
        singleSelect: false,
        optional: true,
        assetIdProperty: 'thirdPartyBillingAccountSetId',
        prompt: 'Select the third party billing groups that apply in this context',
        description: '',
        help: '',
      },
      assetToWorkflowStateMapping: {
        assetProperty: 'thirdPartyBillingAccountSetIds',
      },
      workflowStateToAssetMapping: {
        mappingType: 'link',
        linkAssetType: 'thirdPartyBillingAccountSet',
        linkAssetTypeOverride: 'thirdPartyBillingAccountSet',
        list: true,
      },
    },
    {
      workflowStepKey: 'summary',
      workflowStepNavigationName: 'Configuration Summary',
      workflowComponent: {
        workflowComponentType: 'workflowSummary',
        title: 'Add Fulfillment Context Summary',
        description: 'The FC Context will be created.  Please review your selections.',
        help: '',
      },
      workflowStateToAssetMapping: {
        mappingType: 'ignore',
      },
    },
  ],
};
